import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { FormField } from '@/components/common/FormSection'
import { clientService } from '@/services/client-service'
import { caseService } from '@/services/case-service'
import type { Client } from '@/types/client'
import type { Case } from '@/types/case'
import type { Account, TransactionType } from '@/types/financeV2.types'
import type { TransactionFilters, TransactionStatusFilter } from '@/services/financeV2Service'
import { STATUS_FILTERS } from '@/lib/finance'

export function clientDisplayName(client: Client): string {
  if (client.clientType === 'PERSON') {
    return client.personalData?.fullName
      || `${client.personalData?.firstName || ''} ${client.personalData?.lastName || ''}`.trim()
      || 'Cliente sem nome'
  }
  return client.companyData?.companyName || client.companyData?.tradingName || 'Empresa sem nome'
}

interface Props {
  filters: TransactionFilters
  accounts: Account[]
  onChange: (filters: TransactionFilters) => void
  onClear: () => void
}

/**
 * Filtros da listagem de lançamentos: tipo, situação (a vencer / vencido / em
 * aberto — regra do servidor), conta, cliente, caso, período de vencimento e
 * lançamentos do agente aguardando sócio.
 */
export function TransactionFiltersPanel({ filters, accounts, onChange, onClear }: Props) {
  const [clients, setClients] = useState<Client[]>([])
  const [cases, setCases] = useState<Case[]>([])

  useEffect(() => {
    clientService.getAllClients({}, { page: 1, limit: 1000, sort: 'createdAt:desc' })
      .then((r) => setClients(r.clients || []))
      .catch(() => setClients([]))
  }, [])

  useEffect(() => {
    if (!filters.clientId) { setCases([]); return }
    caseService.getAllCases({ clientId: filters.clientId }, { page: 1, limit: 200, sort: 'createdAt:desc' })
      .then((r) => setCases(r.cases || []))
      .catch(() => setCases([]))
  }, [filters.clientId])

  const set = (patch: Partial<TransactionFilters>) => onChange({ ...filters, ...patch })

  return (
    <Card size="sm">
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label="Tipo" htmlFor="flt-type">
          <NativeSelect id="flt-type" value={filters.type || ''}
            onChange={(e) => set({ type: (e.target.value as TransactionType) || undefined })}>
            <option value="">Todos</option>
            <option value="INCOME">Receita</option>
            <option value="EXPENSE">Despesa</option>
          </NativeSelect>
        </FormField>

        <FormField label="Situação" htmlFor="flt-status">
          <NativeSelect id="flt-status" value={filters.status || ''}
            onChange={(e) => set({ status: (e.target.value as TransactionStatusFilter) || undefined })}>
            <option value="">Todas</option>
            {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </NativeSelect>
        </FormField>

        <FormField label="Conta" htmlFor="flt-account">
          <NativeSelect id="flt-account" value={filters.accountId || ''}
            onChange={(e) => set({ accountId: e.target.value || undefined })}>
            <option value="">Todas</option>
            {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
          </NativeSelect>
        </FormField>

        <FormField label="Cliente" htmlFor="flt-client">
          <NativeSelect id="flt-client" value={filters.clientId || ''}
            onChange={(e) => set({ clientId: e.target.value || undefined, caseId: undefined })}>
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{clientDisplayName(c)}</option>)}
          </NativeSelect>
        </FormField>

        <FormField label="Caso" htmlFor="flt-case">
          <NativeSelect id="flt-case" value={filters.caseId || ''} disabled={!filters.clientId}
            onChange={(e) => set({ caseId: e.target.value || undefined })}>
            <option value="">{filters.clientId ? 'Todos' : 'Escolha o cliente'}</option>
            {cases.map((c) => <option key={c.id} value={c.id}>{c.caseNumber ? `${c.caseNumber} — ${c.title}` : c.title}</option>)}
          </NativeSelect>
        </FormField>

        <FormField label="Vencimento de" htmlFor="flt-from">
          <Input id="flt-from" type="date" value={filters.dueDateFrom || ''}
            onChange={(e) => set({ dueDateFrom: e.target.value || undefined })} />
        </FormField>

        <FormField label="Vencimento até" htmlFor="flt-to">
          <Input id="flt-to" type="date" value={filters.dueDateTo || ''}
            onChange={(e) => set({ dueDateTo: e.target.value || undefined })} />
        </FormField>

        <div className="flex min-w-0 flex-col justify-end gap-2">
          <div className="flex items-center gap-2">
            <input id="flt-needs-confirmation" type="checkbox" checked={filters.needsConfirmation === true}
              onChange={(e) => set({ needsConfirmation: e.target.checked || undefined })}
              className="size-4 rounded-sm border-input accent-primary" />
            <Label htmlFor="flt-needs-confirmation" className="font-normal">Do Hermes, aguardando sócio</Label>
          </div>
          <Button type="button" variant="outline" onClick={onClear} className="w-full">
            Limpar Filtros
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
